import { getLabs } from 'api/labs';
import { fetchSingleStudy } from 'api/study';
import { getUserLabPermissions } from 'api/userCredentials';
import { Permission } from 'interfaces/permissionOption';

export interface ManifestDestination {
  studyId: string;
  labId: string;
  studyName: string;
  labName: string;
}

export const loadManifestDestination = async (
  request: Pick<ManifestDestination, 'studyId' | 'labId'> & { currentLabId: string; canSwitchLabs: boolean },
  signal?: AbortSignal
): Promise<ManifestDestination> => {
  const unavailable = () =>
    new Error('The destination is unavailable or you do not have access to organize its slides.');
  const checkCanceled = () => {
    if (signal?.aborted) throw new DOMException('Destination lookup canceled.', 'AbortError');
  };
  checkCanceled();
  if (request.labId !== request.currentLabId && !request.canSwitchLabs) throw unavailable();
  try {
    const labs = await getLabs({ signal });
    checkCanceled();
    const lab = labs.find((candidate) => candidate.id === request.labId && !candidate.deletedAt);
    if (!lab) throw unavailable();
    const permissions = await getUserLabPermissions(request.labId, signal);
    checkCanceled();
    if (!permissions.includes(Permission.ApplyCaseManifest)) throw unavailable();
    const study = await fetchSingleStudy(request.studyId, request.labId);
    checkCanceled();
    if (study.id !== request.studyId || study.labId !== request.labId) throw unavailable();
    return { studyId: study.id, labId: lab.id, studyName: study.name, labName: lab.name };
  } catch (error) {
    checkCanceled();
    throw unavailable();
  }
};
